import { z } from "zod";
import {
  ALLIANCE_RANKS,
  BUILDING_FAMILIES,
  FACTIONS,
  INVENTORY_CATEGORIES,
  STOCKPILE_RESOURCES,
  type AllianceRank,
  type BuildingFamily,
  type CityTier,
  type Faction,
  type InventoryCategory,
  type StockpileResource,
} from "./enums.js";

const factionEnum = z.enum(FACTIONS as readonly [Faction, ...Faction[]]);
const allianceRankEnum = z.enum(ALLIANCE_RANKS as readonly [AllianceRank, ...AllianceRank[]]);
const buildingFamilyEnum = z.enum(
  BUILDING_FAMILIES as readonly [BuildingFamily, ...BuildingFamily[]],
);
const stockpileEnum = z.enum(
  STOCKPILE_RESOURCES as readonly [StockpileResource, ...StockpileResource[]],
);
const inventoryCategoryEnum = z.enum(
  INVENTORY_CATEGORIES as readonly [InventoryCategory, ...InventoryCategory[]],
);
const cityTierEnum = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]) as z.ZodType<
  CityTier
>;

/** §3.5 server entity. */
export const ServerSchema = z.object({
  id: z.number().int().positive(),
  themeName: z.string().min(1),
  language: z.string().min(2).max(16),
  maxPlayers: z.number().int().positive(),
  currentPlayers: z.number().int().nonnegative(),
  openedAtMs: z.number().int().nonnegative(),
});
export type ServerDto = z.infer<typeof ServerSchema>;

/** §3.2 city entity. */
export const CitySchema = z.object({
  id: z.string().min(1),
  serverId: z.number().int().positive(),
  displayName: z.string().min(1),
  tier: cityTierEnum,
  role: z.enum(["LAND", "NAVAL"]),
  faction: factionEnum,
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  landmarkKind: z.string().optional(),
  loadState: z.enum(["NORMAL", "BUSY", "FULL"]),
});
export type CityDto = z.infer<typeof CitySchema>;

/** §11.6 player entity. */
export const PlayerSchema = z.object({
  id: z.string().uuid(),
  serverId: z.number().int().positive(),
  handle: z.string().min(2).max(32),
  faction: factionEnum,
  baseLevel: z.number().int().min(1).max(32),
  powerRating: z.number().int().nonnegative(),
  allianceTag: z.string().min(1).max(8).nullable(),
  vipRank: z.number().int().min(0).max(16),
  createdAtMs: z.number().int().nonnegative(),
});
export type PlayerDto = z.infer<typeof PlayerSchema>;

/** §4 home base. */
export const BaseSchema = z.object({
  id: z.string().uuid(),
  playerId: z.string().uuid(),
  cityId: z.string().min(1),
  hexCol: z.number().int(),
  hexRow: z.number().int(),
  level: z.number().int().min(1).max(32),
});
export type BaseDto = z.infer<typeof BaseSchema>;

/** §5 building entity. */
export const BuildingSchema = z.object({
  id: z.string().uuid(),
  baseId: z.string().uuid(),
  family: buildingFamilyEnum,
  level: z.number().int().min(0).max(32),
  slotIndex: z.number().int().nonnegative(),
});
export type BuildingDto = z.infer<typeof BuildingSchema>;

/** §7.1 + §7.2 + diamonds player wallet. */
export const PlayerWalletSchema = z.object({
  playerId: z.string().uuid(),
  stockpile: z.record(stockpileEnum, z.number().nonnegative()),
  industrial: z.record(z.string(), z.number().nonnegative()),
  diamonds: z.number().int().nonnegative(),
});
export type PlayerWalletDto = z.infer<typeof PlayerWalletSchema>;

/** §16.2 alliance metadata. */
export const AllianceSchema = z.object({
  id: z.string().uuid(),
  serverId: z.number().int().positive(),
  tag: z.string().min(2).max(8),
  displayName: z.string().min(1).max(32),
  level: z.number().int().min(1).max(30),
  memberCount: z.number().int().nonnegative(),
  memberCap: z.number().int().positive(),
  powerRating: z.number().int().nonnegative(),
});
export type AllianceDto = z.infer<typeof AllianceSchema>;

/** §16.3 alliance member. */
export const AllianceMemberSchema = z.object({
  allianceId: z.string().uuid(),
  playerId: z.string().uuid(),
  rank: allianceRankEnum,
  joinedAtMs: z.number().int().nonnegative(),
});
export type AllianceMemberDto = z.infer<typeof AllianceMemberSchema>;

/** §7.5 inventory item. */
export const InventoryItemSchema = z.object({
  id: z.string().uuid(),
  playerId: z.string().uuid(),
  category: inventoryCategoryEnum,
  itemKey: z.string().min(1),
  count: z.number().int().nonnegative(),
});
export type InventoryItemDto = z.infer<typeof InventoryItemSchema>;
